import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  ArrowLeft, Plus, Edit2, Trash2, Search, Link as LinkIcon,
  Loader2, User, MapPin, Bed, Bath, Square, Check, X, AlertCircle,
  Heart, Calendar, Eye, ChevronLeft, ChevronRight, ExternalLink, Home, MessageSquare,
  ArrowRightLeft
} from 'lucide-react';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription
} from '../../components/ui/sheet';
import { ScrollArea } from '../../components/ui/scroll-area';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Side Panel Component for Admin
const ListingDetailPanel = ({ open, item, items, currentIndex, onClose, onNavigate, onEdit }) => {
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  // Reset image index when item changes
  useEffect(() => {
    setActiveImageIndex(0);
  }, [item?.id]);

  const formatPrice = (amount, currency, intent) => {
    if (!amount) return '';
    const formatted = `${currency === 'MXN' ? 'MX$' : '$'}${amount.toLocaleString()}`;
    return intent === 'rent' ? `${formatted}/mo` : formatted;
  };

  const getStatusBadge = (status) => {
    const styles = {
      suggested: 'bg-[#333] text-[#888]',
      liked: 'bg-green-500/20 text-green-400',
      disliked: 'bg-red-500/20 text-red-400',
      viewing_requested: 'bg-[#2CA8A0]/20 text-[#2CA8A0]'
    };
    const labels = {
      suggested: 'Suggested',
      liked: 'Client Liked',
      disliked: 'Not Interested',
      viewing_requested: 'Viewing Requested'
    };
    return (
      <span className={`px-3 py-1 text-sm font-medium ${styles[status] || styles.suggested}`}>
        {labels[status] || 'Suggested'}
      </span>
    );
  };

  const hasNext = currentIndex < items.length - 1;
  const hasPrev = currentIndex > 0;

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-none sm:w-[55vw] bg-[#F9F6EF] border-l border-[#FFFFFF] p-0 overflow-hidden [&>button]:hidden"
        data-testid="listing-detail-panel"
      >
        <SheetHeader className="sr-only">
          <SheetTitle>{item?.title || 'Listing Detail'}</SheetTitle>
          <SheetDescription>Property listing details</SheetDescription>
        </SheetHeader>

        {/* Panel Header with nav */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#FFFFFF] bg-[#F9F6EF]">
          <div className="flex items-center gap-3">
            <button
              onClick={() => onNavigate('prev')}
              disabled={!hasPrev}
              data-testid="panel-prev-btn"
              className={`p-1.5 transition ${hasPrev ? 'text-[#0A1F2E] hover:bg-[#F9F6EF]' : 'text-[#333] cursor-not-allowed'}`}
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="text-[#666] text-sm tabular-nums">{currentIndex + 1} / {items.length}</span>
            <button
              onClick={() => onNavigate('next')}
              disabled={!hasNext}
              data-testid="panel-next-btn"
              className={`p-1.5 transition ${hasNext ? 'text-[#0A1F2E] hover:bg-[#F9F6EF]' : 'text-[#333] cursor-not-allowed'}`}
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
          <div className="flex items-center gap-2">
            {item && getStatusBadge(item.status)}
            <button
              onClick={() => item && onEdit(item)}
              className="px-3 py-1.5 text-sm bg-[#0A1F2E] text-white hover:bg-[#1A5C6E] transition flex items-center gap-1.5"
              data-testid="panel-edit-btn"
            >
              <Edit2 className="w-3.5 h-3.5" />
              Edit
            </button>
            <button
              onClick={onClose}
              className="p-1.5 text-[#666] hover:text-white transition"
              data-testid="panel-close-btn"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {item && (
          <ScrollArea className="h-[calc(100vh-53px)]">
            {/* Image Gallery */}
            <div className="relative bg-[#111]">
              <div className="aspect-[16/9]">
                {item.images?.length > 0 ? (
                  <img
                    src={item.images[activeImageIndex] || item.images[0]}
                    alt={item.title}
                    className="w-full h-full object-cover"
                    data-testid="panel-main-image"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Home className="w-16 h-16 text-[#333]" />
                  </div>
                )}
              </div>

              {item.images?.length > 0 && (
                <div className="absolute top-3 left-3 bg-black/70 px-2.5 py-1 text-xs text-white">
                  {activeImageIndex + 1} / {item.images.length} photos
                </div>
              )}

              <div className="absolute top-3 right-3">
                <span className={`px-2.5 py-1 text-xs font-medium ${
                  item.intent === 'rent' ? 'bg-blue-500 text-white' : 'bg-green-500 text-white'
                }`}>
                  {item.intent === 'rent' ? 'For Rent' : 'For Sale'}
                </span>
              </div>

              {/* Image navigation arrows on main image */}
              {item.images?.length > 1 && (
                <>
                  <button
                    onClick={() => setActiveImageIndex(i => Math.max(0, i - 1))}
                    className="absolute left-2 top-1/2 -translate-y-1/2 p-1.5 bg-black/60 text-white hover:bg-black/80 transition"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setActiveImageIndex(i => Math.min((item.images?.length || 1) - 1, i + 1))}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 bg-black/60 text-white hover:bg-black/80 transition"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </>
              )}
            </div>

            {/* Thumbnails */}
            {item.images?.length > 1 && (
              <div className="flex gap-1.5 px-5 py-3 overflow-x-auto bg-[#F9F6EF]">
                {item.images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveImageIndex(idx)}
                    className={`w-16 h-12 shrink-0 overflow-hidden border-2 transition ${
                      idx === activeImageIndex ? 'border-[#2CA8A0]' : 'border-transparent opacity-50 hover:opacity-100'
                    }`}
                    data-testid={`panel-thumb-${idx}`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}

            {/* Details */}
            <div className="px-5 py-5 space-y-5">
              <div>
                <h2 className="text-xl text-[#0A1F2E] font-medium mb-1" data-testid="panel-title">{item.title}</h2>
                <p className="text-xl text-[#2CA8A0] font-semibold" data-testid="panel-price">
                  {formatPrice(item.price_amount, item.price_currency, item.intent)}
                </p>
              </div>

              {(item.neighborhood || item.city) && (
                <div className="flex items-center gap-2 text-[#888]">
                  <MapPin className="w-4 h-4" />
                  <span>{item.neighborhood ? `${item.neighborhood}, ` : ''}{item.city}</span>
                </div>
              )}

              {/* Property Facts */}
              <div className="grid grid-cols-3 gap-3 p-4 bg-[#F9F6EF] border border-[#FFFFFF]">
                <div className="text-center">
                  <Bed className="w-5 h-5 text-[#2CA8A0] mx-auto mb-1" />
                  <p className="text-[#0A1F2E] font-medium">{item.bedrooms || '-'}</p>
                  <p className="text-xs text-[#666]">Bedrooms</p>
                </div>
                <div className="text-center">
                  <Bath className="w-5 h-5 text-[#2CA8A0] mx-auto mb-1" />
                  <p className="text-[#0A1F2E] font-medium">{item.bathrooms || '-'}</p>
                  <p className="text-xs text-[#666]">Bathrooms</p>
                </div>
                <div className="text-center">
                  <Square className="w-5 h-5 text-[#2CA8A0] mx-auto mb-1" />
                  <p className="text-[#0A1F2E] font-medium">{item.area_sqm || '-'}</p>
                  <p className="text-xs text-[#666]">m²</p>
                </div>
              </div>

              {/* Mundalo Notes */}
              {item.frg_notes && (
                <div className="p-4 bg-[#2CA8A0]/10 border border-[#2CA8A0]/30">
                  <div className="flex items-center gap-2 text-[#2CA8A0] text-sm mb-2">
                    <MessageSquare className="w-4 h-4" />
                    <span className="font-medium">Your notes to client</span>
                  </div>
                  <p className="text-[#1A5C6E] text-sm">{item.frg_notes}</p>
                </div>
              )}

              {/* Description */}
              {item.description && (
                <div>
                  <h3 className="text-xs text-[#666] uppercase tracking-wider mb-2">Description</h3>
                  <p className="text-[#0A1F2E]/60 text-sm leading-relaxed">{item.description}</p>
                </div>
              )}

              {/* Source Link */}
              {item.source_url && (
                <a
                  href={item.source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm text-[#888] hover:text-[#2CA8A0] transition"
                  data-testid="panel-source-link"
                >
                  <ExternalLink className="w-4 h-4" />
                  View Original Listing
                </a>
              )}

              {/* Client Engagement Section */}
              <div className="border-t border-[#FFFFFF] pt-5 space-y-3" data-testid="panel-engagement">
                <h3 className="text-xs text-[#666] uppercase tracking-wider">Client Engagement</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="p-3 bg-[#F9F6EF] border border-[#FFFFFF]">
                    <p className="text-[#666] text-xs mb-1">Viewed</p>
                    <p className={item.viewed_at ? 'text-blue-400' : 'text-[#555]'}>
                      {item.viewed_at ? `Yes (${item.view_count || 1}x) - ${new Date(item.viewed_at).toLocaleDateString('en-US', { timeZone: 'America/Chicago' })}` : 'Not yet'}
                    </p>
                  </div>
                  <div className="p-3 bg-[#F9F6EF] border border-[#FFFFFF]">
                    <p className="text-[#666] text-xs mb-1">Reaction</p>
                    <p className={item.client_reaction ? 'text-[#2CA8A0] capitalize' : 'text-[#555]'}>
                      {item.client_reaction ? item.client_reaction.replace(/_/g, ' ') : 'None'}
                    </p>
                    {item.client_reaction_at && (
                      <p className="text-[#555] text-xs mt-0.5">{new Date(item.client_reaction_at).toLocaleDateString('en-US', { timeZone: 'America/Chicago' })}</p>
                    )}
                  </div>
                </div>
                {item.client_note && (
                  <div className="p-3 bg-orange-500/5 border border-orange-500/20" data-testid="panel-client-note">
                    <div className="flex items-center gap-2 text-orange-400 text-xs mb-1.5">
                      <MessageSquare className="w-3.5 h-3.5" />
                      Client Note {item.client_note_at && `- ${new Date(item.client_note_at).toLocaleDateString('en-US', { timeZone: 'America/Chicago' })}`}
                    </div>
                    <p className="text-[#ccc] text-sm leading-relaxed">{item.client_note}</p>
                  </div>
                )}
              </div>

              {item.created_at && (
                <p className="text-xs text-[#555]">
                  Added: {new Date(item.created_at).toLocaleDateString('en-US', { timeZone: 'America/Chicago' })}
                </p>
              )}
            </div>
          </ScrollArea>
        )}
      </SheetContent>
    </Sheet>
  );
};

const AdminShortlist = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialUserId = searchParams.get('user_id') || '';
  
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(initialUserId);
  const [clientSearch, setClientSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState(getEmptyForm());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [selectedItemIndex, setSelectedItemIndex] = useState(null);
  
  // Import state
  const [importUrl, setImportUrl] = useState('');
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState('');

  // Transfer state
  const [showTransfer, setShowTransfer] = useState(false);
  const [transferTo, setTransferTo] = useState('');
  const [transferring, setTransferring] = useState(false);
  const [transferResult, setTransferResult] = useState(null);
  const [transferClients, setTransferClients] = useState([]);

  function getEmptyForm() {
    return {
      user_id: '',
      source_url: '',
      title: '',
      city: 'Mexico City',
      neighborhood: '',
      price_amount: '',
      price_currency: 'USD',
      intent: 'rent',
      bedrooms: '',
      bathrooms: '',
      area_sqm: '',
      images: [],
      frg_notes: ''
    };
  }

  useEffect(() => {
    verifyAdmin();
  }, []);

  useEffect(() => {
    if (selectedUserId) {
      fetchShortlistItems();
    } else {
      setItems([]);
    }
  }, [selectedUserId]);

  const verifyAdmin = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/verify`, { credentials: 'include' });
      if (!response.ok) navigate('/admin');
    } catch {
      navigate('/admin');
    }
    setLoading(false);
  };

  const searchClients = async (query) => {
    if (!query || query.length < 2) {
      setClients([]);
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/admin/portal/users/search?search=${encodeURIComponent(query)}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setClients(data.clients || []);
      }
    } catch (err) {
      console.error('Error searching clients:', err);
    }
  };

  const fetchShortlistItems = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/portal/shortlist?user_id=${selectedUserId}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setItems(data.items || []);
      }
    } catch (err) {
      console.error('Error fetching shortlist:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleImportUrl = async () => {
    if (!importUrl.trim() || !formData.user_id) return;
    
    setImporting(true);
    setImportError('');
    
    try {
      const response = await fetch(`${API_URL}/api/admin/portal/shortlist/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ source_url: importUrl, user_id: formData.user_id })
      });
      
      const data = await response.json();
      
      if (data.ok) {
        setFormData(prev => ({
          ...prev,
          source_url: importUrl,
          title: data.title || prev.title,
          description: data.description || '',
          city: data.city || prev.city,
          neighborhood: data.neighborhood || prev.neighborhood,
          price_amount: data.price_amount || prev.price_amount,
          price_currency: data.price_currency || prev.price_currency,
          bedrooms: data.bedrooms || prev.bedrooms,
          bathrooms: data.bathrooms || prev.bathrooms,
          area_sqm: data.area_sqm || prev.area_sqm,
          images: data.images || []
        }));
        setImportUrl('');
      } else {
        setImportError(data.reason || 'Unable to import. Please enter manually.');
      }
    } catch (err) {
      setImportError('Unable to import. Please enter manually.');
    } finally {
      setImporting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    
    try {
      const payload = {
        ...formData,
        price_amount: formData.price_amount ? parseFloat(formData.price_amount) : null,
        bedrooms: formData.bedrooms ? parseInt(formData.bedrooms) : null,
        bathrooms: formData.bathrooms ? parseFloat(formData.bathrooms) : null,
        area_sqm: formData.area_sqm ? parseFloat(formData.area_sqm) : null,
        images: Array.isArray(formData.images) ? formData.images : []
      };
      
      let response;
      if (editingItem) {
        response = await fetch(`${API_URL}/api/admin/portal/shortlist/${editingItem.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(payload)
        });
      } else {
        response = await fetch(`${API_URL}/api/admin/portal/shortlist/create`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(payload)
        });
      }
      
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to save');
      }
      
      setShowForm(false);
      setEditingItem(null);
      setFormData(getEmptyForm());
      
      if (selectedUserId) {
        fetchShortlistItems();
      }
      
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.title}"?`)) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/portal/shortlist/${item.id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.ok) fetchShortlistItems();
    } catch (err) {
      console.error('Error deleting:', err);
    }
  };

  const searchTransferClients = async (query) => {
    if (!query || query.length < 2) { setTransferClients([]); return; }
    try {
      const response = await fetch(`${API_URL}/api/admin/portal/users/search?search=${encodeURIComponent(query)}`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setTransferClients(data.clients || []);
      }
    } catch (err) { console.error(err); }
  };

  const handleTransfer = async (toEmail) => {
    if (!toEmail || !clientSearch) return;
    setTransferring(true);
    setTransferResult(null);
    try {
      const response = await fetch(`${API_URL}/api/admin/portal/shortlist/transfer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ from_email: clientSearch, to_email: toEmail })
      });
      const data = await response.json();
      if (response.ok) {
        setTransferResult({ ok: true, message: data.message });
        // Refresh list (items moved away, so this client's list will be empty)
        fetchShortlistItems();
      } else {
        setTransferResult({ ok: false, message: data.detail || 'Transfer failed' });
      }
    } catch (err) {
      setTransferResult({ ok: false, message: 'Transfer failed' });
    } finally {
      setTransferring(false);
    }
  };

  const formatPrice = (amount, currency, intent) => {
    if (!amount) return '-';
    const formatted = `${currency === 'MXN' ? 'MX$' : '$'}${amount.toLocaleString()}`;
    return intent === 'rent' ? `${formatted}/mo` : formatted;
  };

  const getStatusBadge = (status) => {
    const styles = {
      suggested: 'bg-[#333] text-[#888]',
      liked: 'bg-green-500/20 text-green-400',
      disliked: 'bg-red-500/20 text-red-400',
      viewing_requested: 'bg-[#2CA8A0]/20 text-[#2CA8A0]'
    };
    return (
      <span className={`px-2 py-1 text-xs rounded ${styles[status] || styles.suggested}`}>
        {status === 'viewing_requested' ? 'Viewing Req.' : status}
      </span>
    );
  };

  if (loading && !showForm) {
    return (
      <div className="min-h-screen bg-[#F9F6EF] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#2CA8A0] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F6EF]">
      {/* Header */}
      <div className="border-b border-[#FFFFFF]">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="text-[#0A1F2E]/60 hover:text-[#0A1F2E] transition"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-medium text-white">Client Shortlists</h1>
              <p className="text-sm text-[#666]">Add properties to client portals</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Client Search */}
        <div className="mb-8 p-6 bg-[#F9F6EF] border border-[#FFFFFF]">
          <label className="block text-xs text-[#666] uppercase tracking-wider mb-3">
            Search Client by Email
          </label>
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666]" />
              <input
                type="text"
                value={clientSearch}
                onChange={(e) => {
                  setClientSearch(e.target.value);
                  searchClients(e.target.value);
                }}
                placeholder="Search by email..."
                className="w-full bg-[#F9F6EF] border border-[#222] pl-10 pr-4 py-3 text-white placeholder-[#555] focus:border-[#2CA8A0] focus:outline-none"
              />
              
              {/* Client dropdown */}
              {clients.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-[#111] border border-[#222] max-h-60 overflow-y-auto z-10">
                  {clients.map((client) => (
                    <button
                      key={client.id}
                      onClick={() => {
                        setSelectedUserId(client.id);
                        setClientSearch(client.email);
                        setClients([]);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[#FFFFFF] transition"
                    >
                      {client.picture ? (
                        <img src={client.picture} alt="" className="w-8 h-8 rounded-full" />
                      ) : (
                        <div className="w-8 h-8 bg-[#222] rounded-full flex items-center justify-center">
                          <User className="w-4 h-4 text-[#666]" />
                        </div>
                      )}
                      <div>
                        <p className="text-white text-sm">{client.name || 'No name'}</p>
                        <p className="text-[#0A1F2E]/60 text-xs">{client.email}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            {selectedUserId && (
              <button
                onClick={() => {
                  setShowForm(true);
                  setEditingItem(null);
                  setFormData({ ...getEmptyForm(), user_id: selectedUserId });
                }}
                className="flex items-center gap-2 bg-[#2CA8A0] text-black px-4 py-3 text-sm font-medium hover:bg-[#2CA8A0] transition"
                data-testid="add-shortlist-btn"
              >
                <Plus className="w-4 h-4" />
                Add to Shortlist
              </button>
            )}
          </div>
        </div>

        {/* Selected Client Info */}
        {selectedUserId && !showForm && (
          <div className="mb-6 flex items-center justify-between">
            <div className="flex items-center gap-2 text-[#888]">
              <User className="w-4 h-4" />
              <span>Showing shortlist for: <span className="text-white">{clientSearch}</span></span>
              <span className="text-[#555]">•</span>
              <span>{items.length} items</span>
            </div>
            {items.length > 0 && (
              <button
                onClick={() => { setShowTransfer(!showTransfer); setTransferTo(''); setTransferResult(null); setTransferClients([]); }}
                className="flex items-center gap-2 px-3 py-2 text-sm border border-[#333] text-[#0A1F2E]/60 hover:text-[#0A1F2E] hover:border-[#555] transition"
                data-testid="transfer-btn"
              >
                <ArrowRightLeft className="w-4 h-4" />
                Transfer All
              </button>
            )}
          </div>
        )}

        {/* Transfer Panel */}
        {showTransfer && selectedUserId && !showForm && (
          <div className="mb-6 p-5 bg-[#F9F6EF] border border-[#2CA8A0]/30" data-testid="transfer-panel">
            <h3 className="text-sm text-[#2CA8A0] uppercase tracking-wider mb-3">Transfer all listings to another client</h3>
            <div className="flex gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666]" />
                <input
                  type="text"
                  value={transferTo}
                  onChange={(e) => { setTransferTo(e.target.value); searchTransferClients(e.target.value); }}
                  placeholder="Search target client email..."
                  className="w-full bg-[#F9F6EF] border border-[#222] pl-10 pr-4 py-3 text-white placeholder-[#555] focus:border-[#2CA8A0] focus:outline-none"
                  data-testid="transfer-email-input"
                />
                {transferClients.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-[#111] border border-[#222] max-h-48 overflow-y-auto z-10">
                    {transferClients.map((c) => (
                      <button
                        key={c.id}
                        onClick={() => { setTransferTo(c.email); setTransferClients([]); }}
                        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[#FFFFFF] transition"
                      >
                        {c.picture ? (
                          <img src={c.picture} alt="" className="w-7 h-7 rounded-full" />
                        ) : (
                          <div className="w-7 h-7 bg-[#222] rounded-full flex items-center justify-center">
                            <User className="w-3.5 h-3.5 text-[#666]" />
                          </div>
                        )}
                        <div>
                          <p className="text-white text-sm">{c.name || 'No name'}</p>
                          <p className="text-[#0A1F2E]/60 text-xs">{c.email}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button
                onClick={() => handleTransfer(transferTo)}
                disabled={transferring || !transferTo.includes('@')}
                className="flex items-center gap-2 bg-[#2CA8A0] text-black px-5 py-3 text-sm font-medium hover:bg-[#2CA8A0] transition disabled:opacity-50"
                data-testid="transfer-confirm-btn"
              >
                {transferring ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRightLeft className="w-4 h-4" />}
                Transfer {items.length} items
              </button>
            </div>
            {transferResult && (
              <p className={`mt-3 text-sm ${transferResult.ok ? 'text-green-400' : 'text-red-400'}`} data-testid="transfer-result">
                {transferResult.message}
              </p>
            )}
          </div>
        )}

        {/* Items List */}
        {!showForm && selectedUserId && (
          <div className="space-y-4">
            {items.length === 0 ? (
              <div className="text-center py-16 text-[#666]">
                No shortlist items for this client yet.
              </div>
            ) : (
              items.map((item, index) => (
                <div 
                  key={item.id}
                  onClick={() => setSelectedItemIndex(index)}
                  className="flex gap-4 p-4 bg-[#F9F6EF] border border-[#FFFFFF] hover:border-[#333] transition cursor-pointer"
                >
                  {/* Image */}
                  <div className="w-32 h-24 bg-[#111] shrink-0 overflow-hidden relative">
                    {item.images?.[0] ? (
                      <img src={item.images[0]} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[#333]">
                        <MapPin className="w-6 h-6" />
                      </div>
                    )}
                    {item.images?.length > 1 && (
                      <div className="absolute bottom-1 right-1 bg-black/70 px-1.5 py-0.5 text-xs text-white">
                        +{item.images.length - 1}
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/0 hover:bg-black/40 transition flex items-center justify-center opacity-0 hover:opacity-100">
                      <Eye className="w-5 h-5 text-white" />
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h3 className="text-[#0A1F2E] font-medium truncate">{item.title}</h3>
                        <p className="text-sm text-[#888]">
                          {item.neighborhood && `${item.neighborhood}, `}{item.city}
                        </p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-[#2CA8A0] font-medium">
                          {formatPrice(item.price_amount, item.price_currency, item.intent)}
                        </p>
                        <div className="mt-1">{getStatusBadge(item.status)}</div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4 mt-2 text-[#666] text-sm">
                      {item.bedrooms && <span>{item.bedrooms} bed</span>}
                      {item.bathrooms && <span>{item.bathrooms} bath</span>}
                      {item.area_sqm && <span>{item.area_sqm} m²</span>}
                      <span className={item.intent === 'rent' ? 'text-blue-400' : 'text-green-400'}>
                        {item.intent === 'rent' ? 'Rent' : 'Buy'}
                      </span>
                    </div>
                    
                    {/* Engagement row */}
                    <div className="flex items-center gap-3 mt-2 text-xs">
                      {item.viewed_at && (
                        <span className="text-blue-400 flex items-center gap-1">
                          <Eye className="w-3 h-3" /> Viewed{item.view_count > 1 ? ` (${item.view_count}x)` : ''}
                        </span>
                      )}
                      {item.client_reaction && (
                        <span className="text-[#2CA8A0] capitalize">{item.client_reaction.replace(/_/g, ' ')}</span>
                      )}
                      {item.client_note && (
                        <span className="text-orange-400 flex items-center gap-1">
                          <MessageSquare className="w-3 h-3" /> Has note
                        </span>
                      )}
                      {!item.viewed_at && !item.client_reaction && !item.client_note && (
                        <span className="text-[#555]">Not yet viewed</span>
                      )}
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => {
                        setEditingItem(item);
                        setFormData({
                          ...item,
                          price_amount: item.price_amount || '',
                          bedrooms: item.bedrooms || '',
                          bathrooms: item.bathrooms || '',
                          area_sqm: item.area_sqm || ''
                        });
                        setShowForm(true);
                      }}
                      className="p-2 text-[#666] hover:text-white transition"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(item)}
                      className="p-2 text-[#666] hover:text-red-400 transition"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Add/Edit Form */}
        {showForm && (
          <div className="bg-[#F9F6EF] border border-[#FFFFFF] p-8">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-xl font-medium text-white">
                {editingItem ? 'Edit Shortlist Item' : 'Add to Client Shortlist'}
              </h2>
              <button
                onClick={() => {
                  setShowForm(false);
                  setEditingItem(null);
                  setFormData(getEmptyForm());
                }}
                className="text-[#666] hover:text-white transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Import by URL */}
            {!editingItem && (
              <div className="mb-8 p-6 bg-[#F9F6EF] border border-[#FFFFFF]">
                <h3 className="text-sm text-[#888] uppercase tracking-wider mb-4">Import by URL (Optional)</h3>
                <div className="flex gap-3">
                  <div className="flex-1 flex items-center gap-2 bg-[#111] border border-[#222] px-3">
                    <LinkIcon className="w-4 h-4 text-[#666]" />
                    <input
                      type="url"
                      value={importUrl}
                      onChange={(e) => setImportUrl(e.target.value)}
                      placeholder="Paste listing URL to prefill..."
                      className="flex-1 bg-transparent py-2.5 text-white placeholder-[#555] focus:outline-none"
                    />
                  </div>
                  <button
                    onClick={handleImportUrl}
                    disabled={importing || !importUrl.trim()}
                    className="px-4 py-2 bg-[#222] text-white text-sm hover:bg-[#333] transition disabled:opacity-50"
                  >
                    {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Fetch Details'}
                  </button>
                </div>
                {importError && (
                  <p className="mt-2 text-sm text-[#ff8888] flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    {importError}
                  </p>
                )}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="p-4 bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                  {error}
                </div>
              )}

              {/* Title */}
              <div>
                <label className="block text-xs text-[#666] uppercase tracking-wider mb-2">
                  Title <span className="text-[#2CA8A0]">*</span>
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  className="w-full bg-[#F9F6EF] border border-[#222] px-4 py-3 text-white focus:border-[#2CA8A0] focus:outline-none"
                />
              </div>

              {/* Location */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs text-[#666] uppercase tracking-wider mb-2">City</label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className="w-full bg-[#F9F6EF] border border-[#222] px-4 py-3 text-white focus:border-[#2CA8A0] focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs text-[#666] uppercase tracking-wider mb-2">Neighborhood</label>
                  <input
                    type="text"
                    value={formData.neighborhood}
                    onChange={(e) => setFormData({ ...formData, neighborhood: e.target.value })}
                    className="w-full bg-[#F9F6EF] border border-[#222] px-4 py-3 text-white placeholder-[#555] focus:border-[#2CA8A0] focus:outline-none"
                  />
                </div>
              </div>

              {/* Intent & Price */}
              <div className="grid md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-xs text-[#666] uppercase tracking-wider mb-2">Intent</label>
                  <select
                    value={formData.intent}
                    onChange={(e) => setFormData({ ...formData, intent: e.target.value })}
                    className="w-full bg-[#F9F6EF] border border-[#222] px-4 py-3 text-white focus:border-[#2CA8A0] focus:outline-none"
                  >
                    <option value="rent">Rent</option>
                    <option value="buy">Buy</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-[#666] uppercase tracking-wider mb-2">Price</label>
                  <input
                    type="number"
                    value={formData.price_amount}
                    onChange={(e) => setFormData({ ...formData, price_amount: e.target.value })}
                    className="w-full bg-[#F9F6EF] border border-[#222] px-4 py-3 text-white focus:border-[#2CA8A0] focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs text-[#666] uppercase tracking-wider mb-2">Currency</label>
                  <select
                    value={formData.price_currency}
                    onChange={(e) => setFormData({ ...formData, price_currency: e.target.value })}
                    className="w-full bg-[#F9F6EF] border border-[#222] px-4 py-3 text-white focus:border-[#2CA8A0] focus:outline-none"
                  >
                    <option value="USD">USD</option>
                    <option value="MXN">MXN</option>
                  </select>
                </div>
              </div>

              {/* Property Details */}
              <div className="grid md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-xs text-[#666] uppercase tracking-wider mb-2">Bedrooms</label>
                  <input
                    type="number"
                    value={formData.bedrooms}
                    onChange={(e) => setFormData({ ...formData, bedrooms: e.target.value })}
                    min="0"
                    className="w-full bg-[#F9F6EF] border border-[#222] px-4 py-3 text-white focus:border-[#2CA8A0] focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs text-[#666] uppercase tracking-wider mb-2">Bathrooms</label>
                  <input
                    type="number"
                    value={formData.bathrooms}
                    onChange={(e) => setFormData({ ...formData, bathrooms: e.target.value })}
                    min="0"
                    step="0.5"
                    className="w-full bg-[#F9F6EF] border border-[#222] px-4 py-3 text-white focus:border-[#2CA8A0] focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs text-[#666] uppercase tracking-wider mb-2">Area (m²)</label>
                  <input
                    type="number"
                    value={formData.area_sqm}
                    onChange={(e) => setFormData({ ...formData, area_sqm: e.target.value })}
                    min="0"
                    className="w-full bg-[#F9F6EF] border border-[#222] px-4 py-3 text-white focus:border-[#2CA8A0] focus:outline-none"
                  />
                </div>
              </div>

              {/* Image URL */}
              <div>
                <label className="block text-xs text-[#666] uppercase tracking-wider mb-2">Image URL</label>
                <input
                  type="url"
                  value={formData.images?.[0] || ''}
                  onChange={(e) => setFormData({ ...formData, images: e.target.value ? [e.target.value] : [] })}
                  placeholder="https://..."
                  className="w-full bg-[#F9F6EF] border border-[#222] px-4 py-3 text-white placeholder-[#555] focus:border-[#2CA8A0] focus:outline-none"
                />
              </div>

              {/* Mundalo Notes */}
              <div>
                <label className="block text-xs text-[#666] uppercase tracking-wider mb-2">
                  Mundalo Notes - "Why this fits you"
                </label>
                <textarea
                  value={formData.frg_notes}
                  onChange={(e) => setFormData({ ...formData, frg_notes: e.target.value })}
                  rows={3}
                  placeholder="Explain why this property is a good match for the client..."
                  className="w-full bg-[#F9F6EF] border border-[#222] px-4 py-3 text-white placeholder-[#555] focus:border-[#2CA8A0] focus:outline-none resize-none"
                />
              </div>

              {/* Source URL */}
              <div>
                <label className="block text-xs text-[#666] uppercase tracking-wider mb-2">Source URL</label>
                <input
                  type="url"
                  value={formData.source_url}
                  onChange={(e) => setFormData({ ...formData, source_url: e.target.value })}
                  className="w-full bg-[#F9F6EF] border border-[#222] px-4 py-3 text-white focus:border-[#2CA8A0] focus:outline-none"
                />
              </div>

              {/* Actions */}
              <div className="flex items-center gap-4 pt-6 border-t border-[#FFFFFF]">
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex items-center gap-2 bg-[#2CA8A0] text-black px-6 py-3 font-medium hover:bg-[#2CA8A0] transition disabled:opacity-50"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  {editingItem ? 'Save Changes' : 'Add to Shortlist'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingItem(null);
                    setFormData(getEmptyForm());
                  }}
                  className="px-6 py-3 text-[#0A1F2E]/60 hover:text-[#0A1F2E] transition"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}
        
        {/* Detail Side Panel */}
        <ListingDetailPanel
          open={selectedItemIndex !== null && !!items[selectedItemIndex]}
          item={selectedItemIndex !== null ? items[selectedItemIndex] : null}
          items={items}
          currentIndex={selectedItemIndex ?? 0}
          onClose={() => setSelectedItemIndex(null)}
          onNavigate={(direction) => {
            if (direction === 'next' && selectedItemIndex < items.length - 1) {
              setSelectedItemIndex(selectedItemIndex + 1);
            } else if (direction === 'prev' && selectedItemIndex > 0) {
              setSelectedItemIndex(selectedItemIndex - 1);
            }
          }}
          onEdit={(item) => {
            setSelectedItemIndex(null);
            setEditingItem(item);
            setFormData({
              ...item,
              price_amount: item.price_amount || '',
              bedrooms: item.bedrooms || '',
              bathrooms: item.bathrooms || '',
              area_sqm: item.area_sqm || ''
            });
            setShowForm(true);
          }}
        />
      </div>
    </div>
  );
};

export default AdminShortlist;
